import React, { useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAreasQuery } from 'src/Lca/LCADiagram/Management/hooks';
import { getImpactAreaLabel } from 'src/Lca/LCADiagram/labels';
import { LcaAreaOfImpactSchema } from 'src/Lca/types';

interface GroupedAreaSelectorProps {
  selectedAreaId: number | null;
  onAreaChange: (_areaId: number | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: any;
  disabled?: boolean;
}

interface GroupedAreas {
  [methodName: string]: LcaAreaOfImpactSchema[];
}

export default function GroupedAreaSelector({
  selectedAreaId,
  onAreaChange,
  label,
  size = 'small',
  fullWidth = false,
  sx,
  disabled = false,
}: GroupedAreaSelectorProps) {
  const { t } = useTranslation('lca');
  const areas = useAreasQuery() ?? [];

  // Group areas by method name
  const groupedAreas: GroupedAreas = useMemo(() => {
    return areas.reduce((groups, area) => {
      const methodName = area.method_name || 'Other';
      if (!groups[methodName]) {
        groups[methodName] = [];
      }
      groups[methodName].push(area);
      return groups;
    }, {} as GroupedAreas);
  }, [areas]);

  // Sort method names for consistent ordering
  const sortedMethodNames = useMemo(() => {
    return Object.keys(groupedAreas).sort();
  }, [groupedAreas]);

  // Automatically select the first area when areas are loaded or when no valid selection
  useEffect(() => {
    if (
      areas.length > 0 &&
      areas[0] &&
      (!selectedAreaId || !areas.find((area) => area.id === selectedAreaId))
    ) {
      // Always select the first item from the API response
      onAreaChange(areas[0].id);
    }
  }, [areas, selectedAreaId, onAreaChange]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const { value } = event.target;
    const numericValue =
      typeof value === 'string' && value !== '' ? Number(value) : value;
    const finalValue =
      numericValue === '' || !numericValue ? null : Number(numericValue);
    onAreaChange(finalValue);
  };

  if (areas.length === 0) return null;

  const displayLabel = label || t('impactArea');
  const selectValue = selectedAreaId || '';

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      sx={{ minWidth: 200, ...sx }}
      disabled={disabled}
    >
      <InputLabel>{displayLabel}</InputLabel>
      <Select<string | number>
        value={selectValue}
        label={displayLabel}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              width: 'auto',
              minWidth: '300px',
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {sortedMethodNames.flatMap((methodName) => [
          <MenuItem
            key={`header-${methodName}`}
            disabled
            sx={{
              backgroundColor: '#f5f5f5 !important',
              fontWeight: 'bold',
              fontSize: '0.8125rem',
              lineHeight: 1.8,
              py: 0.5,
              px: 2,
              minHeight: 'auto',
              borderBottom: '1px solid #e0e0e0',
              color: '#333 !important',
              cursor: 'default !important',
              opacity: '1 !important',
            }}
          >
            {methodName}
          </MenuItem>,
          ...groupedAreas[methodName].map((area) => (
            <MenuItem
              key={area.id}
              value={area.id}
              sx={{
                pl: 4,
                pr: 2,
                minHeight: 'auto',
                py: 0.5,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                backgroundColor: 'white',
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                width="100%"
                gap={1}
                sx={{ pointerEvents: 'none' }} // Prevent nested elements from intercepting clicks
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: area.color,
                    flexShrink: 0,
                    mt: 0.125,
                  }}
                />
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      lineHeight: 1.25,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      mb: 0.125,
                    }}
                  >
                    {getImpactAreaLabel(t, area.name)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.6875rem',
                      lineHeight: 1.1,
                      display: 'block',
                    }}
                  >
                    {area.unit_name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
}
